/**
 * CameraController — world-space camera for the combat renderer.
 *
 * # What this file owns (Layer 5)
 *
 *   1. **Spring-smoothed follow** — critically-damped spring (SmoothDamp,
 *      Game Programming Gems 4) tracks a logical target each frame.
 *      Framerate-independent; Unity-equivalent algorithm. No overshoot.
 *   2. **Velocity look-ahead** — camera leads the followed entity in its
 *      direction of motion, falling off to zero at standstill. Look-ahead
 *      offset has its own independent spring so direction changes don't
 *      snap.
 *   3. **Bounds clamping (clamp-target-first)** — clamp the target before
 *      the spring sees it, so the spring never builds up "stored velocity"
 *      against a wall. Edge departures stay smooth.
 *   4. **Letterbox handling** — when a map is smaller than the effective
 *      viewport, snap camera center to map center (letterbox naturally
 *      via worldContainer translation).
 *   5. **Zoom from day one** — `effectiveViewport = viewport / zoom` used
 *      throughout clamp math. Phase 13 (Horde Mode zoom-out as enemies
 *      multiply) and Phase 14+ (replay cinematic moments) work without
 *      rework.
 *   6. **Caller-provided delta** — `update(combatState, deltaSeconds)`.
 *      Wallclock delta in combat (PixiJS ticker), simulated delta in
 *      replay mode (`1/replayFps`). Same code path; bit-exact replay
 *      video possible.
 *   7. **Resolver-as-function follow target** — `FollowTargetResolver`
 *      is `(combatState) => { x, y } | null`. Single-entity, midpoint,
 *      fixed lookat, boss intro, spectator all use the same API.
 *      Pre-built resolvers cover the 95% case ergonomically.
 *   8. **Lifecycle** — attach/dispose with idempotent dispose, defensive
 *      no-op `update()` when not attached or after dispose, throw-on-
 *      misuse for attach-after-dispose.
 *
 * # What this file does NOT own
 *
 *   - World container construction (Layer 6 — CombatScreen wires
 *     `app.stage > worldContainer > [tileRenderer, spriteRenderer]`).
 *     CameraController only translates a Container the caller passes in.
 *   - PixiJS Application lifecycle (Layer 1 — PixiMount).
 *   - Tile / sprite rendering (Layers 3, 4a, 4b).
 *   - Per-frame ticker registration — Layer 6's CombatScreen registers
 *     `app.ticker.add(...)` and calls `cameraController.update(...)` from
 *     within. CameraController is not React-aware.
 *
 * # Why critically-damped spring (SmoothDamp), not lerp
 *
 * Naive lerp `pos += (target - pos) * 0.15` has a bug: it's
 * framerate-dependent. At 60Hz it feels different from 144Hz different
 * from 30Hz. High-refresh monitors get a snappier camera; low-refresh
 * feel sluggish.
 *
 * Critically-damped spring is the unique solution that reaches the
 * target in minimum time WITHOUT overshooting. It's the canonical AAA
 * pick — Hades, Celeste, Hollow Knight, Unity's `Vector3.SmoothDamp`
 * all use the same algorithm. Tunable parameter is `smoothTime`
 * (seconds-to-target), which is intuitive to design with: "0.15s smooth
 * time" means "after ~0.15s, camera is essentially at target."
 *
 * The state cost is two extra numbers per CameraController (velocity
 * tracking on each axis). Negligible.
 *
 * # Why velocity look-ahead
 *
 * Pure smoothing follows velocity — the camera lags behind the player
 * by however much smoothing eats. In dodge-based action where the
 * player snaps directions instantly, the camera trails behind.
 *
 * Look-ahead samples player velocity, projects camera target slightly
 * ahead in direction of motion. Hades, Dead Cells, Hollow Knight all
 * do this. Magnitude is small (default 80 render pixels at full speed).
 * Transforms feel: camera anticipates motion instead of trailing it.
 *
 * Look-ahead has its own independent spring (`lookAheadSmoothTime`)
 * so direction changes don't snap the look-ahead vector.
 *
 * # Why clamp-target-first (not clamp-after-smoothing)
 *
 * Naive: smooth toward raw target, clamp result. Bug: spring builds up
 * "stored velocity" pushing toward unclamped target while position is
 * pinned at the wall. When the player walks away from the edge, camera
 * jerks free. This is the early-Hades / early-Dead-Cells bug; both
 * fixed it the same way.
 *
 * Fix: clamp the TARGET first, then smooth toward the clamped target.
 * Spring never sees an out-of-bounds target. Smooth on both edge
 * approach AND departure.
 *
 * # Why caller-provided delta (not wallclock-internal)
 *
 * Camera is a render concern, so wallclock-ms feels right. But replays
 * (Phase 14+) need deterministic camera footage — same run produces
 * same video. If camera reads `Date.now()` internally, replays at 0.5x
 * or 2x playback produce different camera footage.
 *
 * Caller-provided delta solves this with zero cost: in normal combat,
 * Layer 6 passes `app.ticker.deltaMS / 1000`. In replay mode, caller
 * passes `1 / replayFps`. Camera doesn't know or care which.
 *
 * # Why FollowTargetResolver as function (not entityId)
 *
 * Single-entity-id covers 95% of cases. The 5% that breaks: boss intros
 * (follow boss briefly), midpoint between two players (split-screen
 * fallback for raids), fixed lookat (hub cursor, cinematic), spectator/
 * replay (follow any entity dynamically), interpolated multi-target
 * (camera weight between player and lock-on target).
 *
 * `FollowTargetResolver = (combatState) => { x, y } | null` covers all
 * of these. Returning `null` = "no valid target this frame, hold last
 * valid position" — defensive against entity despawn mid-frame.
 *
 * Pre-built resolvers (`followPlayer`, `followEntityById`,
 * `followMidpoint`, `followFixedPoint`) keep ergonomics for common
 * cases.
 *
 * # Lifecycle
 *
 *   1. Construct with options (viewport size, smoothing parameters,
 *      optional bounds, optional initial follow target)
 *   2. Call `attach(worldContainer)` — typically once, in Layer 6
 *      after PixiMount mounts and the world container is constructed
 *   3. Call `update(combatState, deltaSeconds)` once per render frame
 *      from the PixiJS ticker
 *   4. Call setters (`setFollowTarget`, `setBounds`, `setViewportSize`,
 *      `setZoom`) as game state demands
 *   5. Call `snapToTarget(combatState)` for instant transitions (scene
 *      changes, respawn, fast-travel)
 *   6. Call `dispose()` on teardown
 *
 * # Test contract
 *
 * The companion test mocks `pixi.js`'s Container at its public surface
 * (per v2.15 #5: one-level-deep sibling mock). Tests verify:
 *   - Lifecycle (attach/dispose idempotency, attach-after-dispose
 *     throws, update before attach is no-op)
 *   - SmoothDamp math (framerate-independent: same end state at 4×32ms
 *     vs 8×16ms; velocity decays; reaches target within smoothTime)
 *   - Look-ahead saturation (zero velocity = zero offset; max velocity
 *     = full distance; above max = still full)
 *   - Clamp-target-first (large map clamps at edges, small map snaps
 *     to map center, bounds=null no clamp, zoom affects effective
 *     viewport)
 *   - Resolver-as-function (followPlayer/EntityById/Midpoint/FixedPoint
 *     all work; null return holds last position; resolver swap mid-
 *     update has no jitter)
 *   - snapToTarget zeros velocities + look-ahead state
 *   - Setters re-apply transform correctly
 *
 * @see {@link ./pixiMount} — PixiJS Application owner; provides
 *      `app.stage` and `app.ticker.deltaMS`
 * @see {@link ./tileRenderer} — produces a Container Layer 6 will
 *      attach to the world container CameraController translates
 * @see {@link ./spriteRenderer} — same
 * @see {@link ../components/CombatScreen} — Layer 6 wires CameraController
 *      into the world container; calls `update` per frame
 * @see {@link ../../core/state/types.ts} — CombatState / CombatEntity
 */

import { type Container } from 'pixi.js';
import {
  fpToFloat,
  type CombatState,
  type EntityId,
} from '@aevons-wake/core';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the camera's logical target each frame.
 *
 * Returns world-space coordinates (in render-pixel space, NOT
 * fixed-point) for where the camera should center, or `null` to hold
 * the last valid position (e.g. follow target despawned mid-frame).
 */
export type FollowTargetResolver = (
  combatState: CombatState,
) => { readonly x: number; readonly y: number } | null;

/**
 * Pre-built resolver: follow the player entity (CombatState.player).
 * The 95% case for single-player roguelite combat.
 */
export function followPlayer(): FollowTargetResolver {
  return (cs) => ({
    x: fpToFloat(cs.player.posX),
    y: fpToFloat(cs.player.posY),
  });
}

/**
 * Pre-built resolver: follow a specific entity by id.
 *
 * Returns `null` if the entity isn't in `combatState.entities` (e.g.
 * despawned). The camera holds last valid position.
 *
 * Use cases: boss intro (follow boss for a beat before zooming back),
 * spectator mode (follow any entity), death cam (follow killer briefly).
 */
export function followEntityById(entityId: EntityId): FollowTargetResolver {
  return (cs) => {
    const entity = cs.entities.find((e) => e.id === entityId);
    if (entity === undefined) return null;
    return { x: fpToFloat(entity.posX), y: fpToFloat(entity.posY) };
  };
}

/**
 * Pre-built resolver: midpoint between two entities.
 *
 * Returns `null` if either entity isn't found. Use case: cinematic
 * tension shots (player + boss), future split-screen-fallback for
 * raids, lock-on targeting visual feedback.
 */
export function followMidpoint(
  idA: EntityId,
  idB: EntityId,
): FollowTargetResolver {
  return (cs) => {
    const a = cs.entities.find((e) => e.id === idA);
    const b = cs.entities.find((e) => e.id === idB);
    if (a === undefined || b === undefined) return null;
    return {
      x: (fpToFloat(a.posX) + fpToFloat(b.posX)) / 2,
      y: (fpToFloat(a.posY) + fpToFloat(b.posY)) / 2,
    };
  };
}

/**
 * Pre-built resolver: fixed world-space point.
 *
 * Use case: hub camera focal point, cinematic establishing shot, debug
 * tooling. Coordinates are in render-pixel world space (post-fpToFloat).
 */
export function followFixedPoint(x: number, y: number): FollowTargetResolver {
  const point = { x, y };
  return () => point;
}

/**
 * Pixel-space rectangular bounds the camera center is clamped within.
 * Layer 6 typically computes this from
 * `tileMap.width * TILE_LOGICAL_WIDTH` etc.
 */
export interface PixelBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface CameraControllerOptions {
  readonly viewportWidth: number;
  readonly viewportHeight: number;

  /**
   * Smooth time in SECONDS — approximate time for the spring to reach
   * the target. Lower = snappier, higher = floatier. Genre default
   * 0.12-0.18 for top-down action roguelites. Defaults to 0.15.
   */
  readonly smoothTime?: number;

  /**
   * Velocity look-ahead distance in render pixels. The camera leads
   * the followed entity in direction of motion when at full velocity.
   * Set to 0 to disable look-ahead entirely. Defaults to 80.
   */
  readonly lookAheadDistance?: number;

  /**
   * Velocity (in fixed-point units per second) at which look-ahead
   * reaches `lookAheadDistance`. Above this speed, look-ahead saturates.
   * Defaults to 5000 (matches Vector Architecture's expected sprint
   * speed at the time of writing; tunable in design pass).
   */
  readonly lookAheadMaxVelocityFp?: number;

  /**
   * How quickly the look-ahead offset itself smooths in (separate spring
   * from the main camera spring). Prevents look-ahead from snapping when
   * direction changes. In seconds. Defaults to 0.25.
   */
  readonly lookAheadSmoothTime?: number;

  /**
   * Initial zoom multiplier. 1.0 = no zoom. Camera math uses
   * `effectiveViewport = viewport / zoom` internally. Defaults to 1.0.
   */
  readonly initialZoom?: number;

  /** Bounds in world-pixel space; if omitted, camera is unbounded. */
  readonly bounds?: PixelBounds;

  /** Initial follow target. Can be set later via `setFollowTarget`. */
  readonly initialFollowTarget?: FollowTargetResolver;
}

// ─────────────────────────────────────────────────────────────────────────────
// SmoothDamp — critically-damped spring (Game Programming Gems 4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of a single SmoothDamp step. The caller updates BOTH the value
 * AND the velocity into its persistent state.
 */
export interface SmoothDampResult {
  readonly value: number;
  readonly velocity: number;
}

/**
 * Critically-damped spring smoothing. Reaches `target` in approximately
 * `smoothTime` seconds without overshooting. Framerate-independent.
 *
 * Standard implementation from Game Programming Gems 4, also used by
 * Unity's `Vector3.SmoothDamp`. The polynomial expansion approximates
 * `exp(-omega * deltaTime)` cheaply and exactly enough for game work.
 *
 * Exported for tests and for any future consumer that wants the same
 * smoothing primitive (e.g., HUD value tweens, zoom transitions).
 *
 * @param current Current value
 * @param target Desired target value
 * @param velocity Current velocity (caller maintains across calls)
 * @param smoothTime Approximate seconds to reach target
 * @param deltaTime Elapsed seconds since last call
 * @returns New value AND new velocity; caller stores both
 */
export function smoothDamp(
  current: number,
  target: number,
  velocity: number,
  smoothTime: number,
  deltaTime: number,
): SmoothDampResult {
  // Guard against zero/negative smoothTime (would divide by zero in
  // omega computation). Floor at a tiny positive value — effectively
  // "snap immediately" without crashing.
  const safeSmoothTime = Math.max(smoothTime, 0.0001);
  const omega = 2 / safeSmoothTime;

  const x = omega * deltaTime;
  // Polynomial approximation of exp(-x). Accurate to ~1e-5 over the
  // range we care about (deltaTime up to a few hundred ms).
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);

  const change = current - target;
  const temp = (velocity + omega * change) * deltaTime;
  const newVelocity = (velocity - omega * temp) * exp;
  const newValue = target + (change + temp) * exp;

  return { value: newValue, velocity: newVelocity };
}

// ─────────────────────────────────────────────────────────────────────────────
// CameraController
// ─────────────────────────────────────────────────────────────────────────────

export class CameraController {
  // Owned references
  private worldContainer: Container | null = null;

  // Spring state — main camera position
  private centerX = 0;
  private centerY = 0;
  private velocityX = 0;
  private velocityY = 0;

  // Spring state — look-ahead offset
  private currentLookAheadX = 0;
  private currentLookAheadY = 0;
  private lookAheadVelocityX = 0;
  private lookAheadVelocityY = 0;

  // Last-valid follow position (used when resolver returns null)
  private lastValidTargetX = 0;
  private lastValidTargetY = 0;
  private hasEverHadValidTarget = false;

  // Configuration
  private viewportWidth: number;
  private viewportHeight: number;
  private readonly smoothTime: number;
  private readonly lookAheadDistance: number;
  private readonly lookAheadMaxVelocityFp: number;
  private readonly lookAheadSmoothTime: number;
  private zoom: number;
  private bounds: PixelBounds | null;
  private followTarget: FollowTargetResolver | null;

  // Lifecycle
  private attached = false;
  private disposed = false;

  constructor(options: CameraControllerOptions) {
    if (options.viewportWidth <= 0 || options.viewportHeight <= 0) {
      throw new Error(
        'CameraController: viewportWidth and viewportHeight must be positive',
      );
    }
    if (options.initialZoom !== undefined && options.initialZoom <= 0) {
      throw new Error('CameraController: initialZoom must be positive');
    }

    this.viewportWidth = options.viewportWidth;
    this.viewportHeight = options.viewportHeight;
    this.smoothTime = options.smoothTime ?? 0.15;
    this.lookAheadDistance = options.lookAheadDistance ?? 80;
    this.lookAheadMaxVelocityFp = options.lookAheadMaxVelocityFp ?? 5000;
    this.lookAheadSmoothTime = options.lookAheadSmoothTime ?? 0.25;
    this.zoom = options.initialZoom ?? 1.0;
    this.bounds = options.bounds ?? null;
    this.followTarget = options.initialFollowTarget ?? null;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Attach the camera to a world Container. Typically called once by
   * Layer 6 after PixiMount mounts and the world container is built.
   *
   * The world container is the parent of tile + sprite renderer
   * containers; CameraController translates IT (and not `app.stage`) so
   * UI overlays added to `app.stage` directly stay screen-fixed.
   *
   * @throws Error if called after dispose() or already attached
   */
  attach(worldContainer: Container): void {
    if (this.disposed) {
      throw new Error(
        'CameraController.attach: cannot attach after dispose; construct a new instance',
      );
    }
    if (this.attached) {
      throw new Error(
        'CameraController.attach: already attached; detach first or construct a new instance',
      );
    }
    this.worldContainer = worldContainer;
    this.attached = true;
    // Apply current state immediately so first frame isn't at (0,0).
    this.applyTransformToContainer();
  }

  /**
   * Tear down. Idempotent. Releases the worldContainer reference (does
   * NOT destroy it — Layer 6 owns its lifecycle), clears follow target,
   * and marks the instance permanently retired.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.attached = false;
    this.worldContainer = null;
    this.followTarget = null;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Per-frame
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Update the camera. Pass `deltaSeconds` (NOT deltaMs) — seconds is
   * the natural unit for spring math.
   *
   * Caller pattern:
   *   - Combat: `cameraController.update(state, app.ticker.deltaMS / 1000)`
   *   - Replay: `cameraController.update(state, 1 / replayFps)`
   *
   * No-op when not attached or after dispose. No-op when deltaSeconds
   * is non-positive (defensive against pause / tab-blur / zero-delta
   * frames PixiJS occasionally emits).
   */
  update(combatState: CombatState, deltaSeconds: number): void {
    if (this.disposed || !this.attached) return;
    if (deltaSeconds <= 0) return;

    // 1. Resolve target via callback
    let targetX = this.lastValidTargetX;
    let targetY = this.lastValidTargetY;

    if (this.followTarget !== null) {
      const resolved = this.followTarget(combatState);
      if (resolved !== null) {
        targetX = resolved.x;
        targetY = resolved.y;
        this.lastValidTargetX = targetX;
        this.lastValidTargetY = targetY;
        this.hasEverHadValidTarget = true;
      }
      // resolved === null: hold last valid (or origin if never had one)
    }

    // 2. Compute look-ahead from player velocity
    const targetLookAhead = this.computeLookAheadOffset(combatState);

    // 3. Smooth the look-ahead offset (independent spring)
    const laxResult = smoothDamp(
      this.currentLookAheadX,
      targetLookAhead.x,
      this.lookAheadVelocityX,
      this.lookAheadSmoothTime,
      deltaSeconds,
    );
    this.currentLookAheadX = laxResult.value;
    this.lookAheadVelocityX = laxResult.velocity;

    const layResult = smoothDamp(
      this.currentLookAheadY,
      targetLookAhead.y,
      this.lookAheadVelocityY,
      this.lookAheadSmoothTime,
      deltaSeconds,
    );
    this.currentLookAheadY = layResult.value;
    this.lookAheadVelocityY = layResult.velocity;

    // 4. Apply look-ahead to target, THEN clamp (clamp-target-first)
    const desiredX = targetX + this.currentLookAheadX;
    const desiredY = targetY + this.currentLookAheadY;
    const clamped = this.clampTarget(desiredX, desiredY);

    // 5. Smooth the camera center toward the clamped target
    const cxResult = smoothDamp(
      this.centerX,
      clamped.x,
      this.velocityX,
      this.smoothTime,
      deltaSeconds,
    );
    this.centerX = cxResult.value;
    this.velocityX = cxResult.velocity;

    const cyResult = smoothDamp(
      this.centerY,
      clamped.y,
      this.velocityY,
      this.smoothTime,
      deltaSeconds,
    );
    this.centerY = cyResult.value;
    this.velocityY = cyResult.velocity;

    // 6. Apply transform to world container
    this.applyTransformToContainer();
  }

  /**
   * Snap the camera instantly to the current resolved target.
   *
   * Use cases: scene transitions (entering a run, hub-to-combat),
   * respawn, fast-travel, replay seek. Zeros all spring + look-ahead
   * state so the next `update()` doesn't smooth from the old position.
   *
   * No-op when not attached or after dispose, or when no follow target
   * is set and no last-valid-target exists.
   */
  snapToTarget(combatState: CombatState): void {
    if (this.disposed || !this.attached) return;

    if (this.followTarget !== null) {
      const resolved = this.followTarget(combatState);
      if (resolved !== null) {
        this.lastValidTargetX = resolved.x;
        this.lastValidTargetY = resolved.y;
        this.hasEverHadValidTarget = true;
      }
    }

    // If we still have no valid target, default to (0, 0) — the only
    // safe pick. Caller can setFollowTarget then snap again.
    const baseX = this.hasEverHadValidTarget ? this.lastValidTargetX : 0;
    const baseY = this.hasEverHadValidTarget ? this.lastValidTargetY : 0;

    const clamped = this.clampTarget(baseX, baseY);
    this.centerX = clamped.x;
    this.centerY = clamped.y;
    this.velocityX = 0;
    this.velocityY = 0;
    this.currentLookAheadX = 0;
    this.currentLookAheadY = 0;
    this.lookAheadVelocityX = 0;
    this.lookAheadVelocityY = 0;
    this.applyTransformToContainer();
  }

  // ───────────────────────────────────────────────────────────────────────
  // Setters
  // ───────────────────────────────────────────────────────────────────────

  setFollowTarget(target: FollowTargetResolver | null): void {
    if (this.disposed) return;
    this.followTarget = target;
  }

  setBounds(bounds: PixelBounds | null): void {
    if (this.disposed) return;
    this.bounds = bounds;
    if (this.attached) {
      // Re-clamp current center against new bounds. Avoid camera being
      // out-of-bounds for a frame after a bounds change.
      const reclamped = this.clampTarget(this.centerX, this.centerY);
      this.centerX = reclamped.x;
      this.centerY = reclamped.y;
      this.applyTransformToContainer();
    }
  }

  setViewportSize(width: number, height: number): void {
    if (this.disposed) return;
    if (width <= 0 || height <= 0) {
      throw new Error(
        'CameraController.setViewportSize: width and height must be positive',
      );
    }
    this.viewportWidth = width;
    this.viewportHeight = height;
    if (this.attached) {
      // Re-clamp + re-apply: viewport change can put camera out of
      // bounds (e.g. browser shrunk past where the camera was clamped).
      const reclamped = this.clampTarget(this.centerX, this.centerY);
      this.centerX = reclamped.x;
      this.centerY = reclamped.y;
      this.applyTransformToContainer();
    }
  }

  setZoom(zoom: number): void {
    if (this.disposed) return;
    if (zoom <= 0) {
      throw new Error('CameraController.setZoom: zoom must be positive');
    }
    this.zoom = zoom;
    if (this.attached) {
      const reclamped = this.clampTarget(this.centerX, this.centerY);
      this.centerX = reclamped.x;
      this.centerY = reclamped.y;
      this.applyTransformToContainer();
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // Test introspection (do not use in production code)
  // ───────────────────────────────────────────────────────────────────────

  _getCenterForTests(): { x: number; y: number } {
    return { x: this.centerX, y: this.centerY };
  }

  _getVelocityForTests(): { x: number; y: number } {
    return { x: this.velocityX, y: this.velocityY };
  }

  _getLookAheadForTests(): { x: number; y: number } {
    return { x: this.currentLookAheadX, y: this.currentLookAheadY };
  }

  _getZoomForTests(): number {
    return this.zoom;
  }

  _getBoundsForTests(): PixelBounds | null {
    return this.bounds;
  }

  _getStateForTests(): 'pre-attach' | 'attached' | 'disposed' {
    if (this.disposed) return 'disposed';
    if (this.attached) return 'attached';
    return 'pre-attach';
  }

  // ───────────────────────────────────────────────────────────────────────
  // Internals
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Compute the velocity-look-ahead offset for the current frame,
   * normalized to fall in `[0, lookAheadDistance]` magnitude.
   *
   * Uses player velocity (combatState.player.velX/velY). Float math is
   * fine here — this is render-side, not combat reducer math.
   */
  private computeLookAheadOffset(
    combatState: CombatState,
  ): { x: number; y: number } {
    if (this.lookAheadDistance <= 0) return { x: 0, y: 0 };

    const player = combatState.player;
    const velFpX = player.velX;
    const velFpY = player.velY;
    const speedFpSq = velFpX * velFpX + velFpY * velFpY;

    if (speedFpSq === 0) return { x: 0, y: 0 };

    const maxFpSq = this.lookAheadMaxVelocityFp * this.lookAheadMaxVelocityFp;
    const speedRatio = speedFpSq >= maxFpSq ? 1 : Math.sqrt(speedFpSq / maxFpSq);

    const speedFp = Math.sqrt(speedFpSq);
    const dirX = velFpX / speedFp;
    const dirY = velFpY / speedFp;

    return {
      x: dirX * speedRatio * this.lookAheadDistance,
      y: dirY * speedRatio * this.lookAheadDistance,
    };
  }

  /**
   * Clamp a target world-coord against bounds, accounting for zoom.
   *
   * When the map is smaller than the effective viewport on an axis,
   * snap that axis to the map's center (letterbox naturally appears
   * via worldContainer translation).
   */
  private clampTarget(
    x: number,
    y: number,
  ): { x: number; y: number } {
    if (this.bounds === null) return { x, y };

    const effectiveHalfW = this.viewportWidth / (2 * this.zoom);
    const effectiveHalfH = this.viewportHeight / (2 * this.zoom);

    const mapW = this.bounds.maxX - this.bounds.minX;
    const mapH = this.bounds.maxY - this.bounds.minY;

    let clampedX: number;
    if (mapW < effectiveHalfW * 2) {
      // Map smaller than effective viewport on X — center on map midpoint
      clampedX = this.bounds.minX + mapW / 2;
    } else {
      clampedX = clamp(
        x,
        this.bounds.minX + effectiveHalfW,
        this.bounds.maxX - effectiveHalfW,
      );
    }

    let clampedY: number;
    if (mapH < effectiveHalfH * 2) {
      clampedY = this.bounds.minY + mapH / 2;
    } else {
      clampedY = clamp(
        y,
        this.bounds.minY + effectiveHalfH,
        this.bounds.maxY - effectiveHalfH,
      );
    }

    return { x: clampedX, y: clampedY };
  }

  /**
   * Apply the current camera state to the world container. Translates
   * such that (centerX, centerY) lands at the screen center, and scales
   * by zoom.
   *
   * Equation derivation:
   *   We want world point (centerX, centerY) to render at screen point
   *   (viewportWidth/2, viewportHeight/2).
   *   Container transform:  screenPos = worldPos * zoom + container.position
   *   Solve for container.position:
   *     viewportWidth/2 = centerX * zoom + container.position.x
   *     container.position.x = viewportWidth/2 - centerX * zoom
   */
  private applyTransformToContainer(): void {
    if (this.worldContainer === null) return;
    this.worldContainer.scale.set(this.zoom);
    this.worldContainer.position.set(
      this.viewportWidth / 2 - this.centerX * this.zoom,
      this.viewportHeight / 2 - this.centerY * this.zoom,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function clamp(value: number, lo: number, hi: number): number {
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}